import React, { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import {
  AlertCircle, Loader2, Eye,
  HelpCircle, Users, UserPlus, Home, Heart, CreditCard, FileText
} from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import {
  IntentStep as _IntentStep,
  SelectStudentsStep as _SelectStudentsStep,
  NewStudentsStep as _NewStudentsStep,
  FamilyInfoStep as _FamilyInfoStep,
  StudentMedicalStep as _StudentMedicalStep,
  TuitionStep as _TuitionStep,
  ReviewStep as _ReviewStep,
} from '~/pages/ParentPortal/steps';

// Cast JSX step components to accept any props (not yet converted to TypeScript)
const IntentStep = _IntentStep as React.ComponentType<any>;
const SelectStudentsStep = _SelectStudentsStep as React.ComponentType<any>;
const NewStudentsStep = _NewStudentsStep as React.ComponentType<any>;
const FamilyInfoStep = _FamilyInfoStep as React.ComponentType<any>;
const StudentMedicalStep = _StudentMedicalStep as React.ComponentType<any>;
const TuitionStep = _TuitionStep as React.ComponentType<any>;
const ReviewStep = _ReviewStep as React.ComponentType<any>;
import { ReenrollmentFormForAdminQuery } from './useReenrollmentFormForAdmin';
import type { AdminReenrollmentReviewProps } from './types';

const TABS = [
  { id: 'intent', label: 'Intent', icon: HelpCircle },
  { id: 'students', label: 'Students', icon: Users },
  { id: 'new-students', label: 'New Students', icon: UserPlus },
  { id: 'family', label: 'Family', icon: Home },
  { id: 'medical', label: 'Medical', icon: Heart },
  { id: 'tuition', label: 'Tuition', icon: CreditCard },
  { id: 'summary', label: 'Summary', icon: FileText },
];

export function AdminReenrollmentReview({ familyEnrollmentId, open, onClose }: AdminReenrollmentReviewProps) {
  const [activeTab, setActiveTab] = useState('intent');

  const { data, loading, error } = useQuery(ReenrollmentFormForAdminQuery, {
    variables: { familyEnrollmentId },
    skip: !familyEnrollmentId || !open,
  });

  const formInfo = data?.reenrollmentFormForAdmin;

  // Form data for display (read-only, no editing)
  // formData is typed as `unknown` by gql.tada (JSON scalar); cast to any for step components
  const formData = formInfo?.formData as Record<string, any> | undefined;

  // No-op update function since forms are read-only
  const updateFormData = useCallback(() => {}, []);

  const handleClose = () => {
    setActiveTab('intent');
    onClose?.();
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not submitted';
    return new Date(dateString).toLocaleString();
  };

  const getStateColor = (state: string) => {
    switch (state) {
      case 'DRAFT': return 'secondary' as const;
      case 'SUBMITTED': return 'default' as const;
      case 'APPROVED': return 'default' as const;
      default: return 'outline' as const;
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                {formInfo?.familyEnrollment?.enrollmentKind === 'RE_ENROLLMENT' ? 'Re-enrollment' : 'Admissions'} Form Review
                {formInfo && (
                  <Badge variant={getStateColor(formInfo.formState)}>
                    {formInfo.formState}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                {formInfo?.familyEnrollment?.family?.name} - {formInfo?.schoolYearName}
                {(formInfo?.submittedAt as string | null) && (
                  <span className="ml-2">
                    • Submitted: {formatDate(formInfo?.submittedAt as string | null)}
                  </span>
                )}
              </DialogDescription>
            </div>
            <Badge variant="secondary" className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              Read Only
            </Badge>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-12 text-destructive">
              <AlertCircle className="h-5 w-5 mr-2" />
              Failed to load form data
            </div>
          )}

          {formInfo && formData && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full justify-start flex-wrap h-auto gap-1 mb-4">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <TabsTrigger key={tab.id} value={tab.id} className="gap-1">
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              <div className="px-1">
                <TabsContent value="intent" className="mt-0">
                  <IntentStep
                    formData={formData}
                    schoolYearName={formInfo.schoolYearName}
                    updateFormData={updateFormData}
                  />
                </TabsContent>

                <TabsContent value="students" className="mt-0">
                  <SelectStudentsStep
                    formData={formData}
                    gradeLevels={formInfo.gradeLevels}
                    updateFormData={updateFormData}
                  />
                </TabsContent>

                <TabsContent value="new-students" className="mt-0">
                  <NewStudentsStep
                    formData={formData}
                    gradeLevels={formInfo.gradeLevels}
                    updateFormData={updateFormData}
                  />
                </TabsContent>

                <TabsContent value="family" className="mt-0">
                  <FamilyInfoStep
                    formData={formData}
                    updateFormData={updateFormData}
                  />
                </TabsContent>

                <TabsContent value="medical" className="mt-0">
                  <StudentMedicalStep
                    formData={formData}
                    updateFormData={updateFormData}
                  />
                </TabsContent>

                <TabsContent value="tuition" className="mt-0">
                  <TuitionStep
                    formData={formData}
                    tuitionOptions={formInfo.tuitionOptions}
                    paymentOptions={formInfo.paymentOptions}
                    updateFormData={updateFormData}
                  />
                </TabsContent>

                <TabsContent value="summary" className="mt-0">
                  <ReviewStep
                    formData={formData}
                    updateFormData={updateFormData}
                  />
                </TabsContent>
              </div>
            </Tabs>
          )}

          {formInfo && !formData && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <FileText className="h-5 w-5 mr-2" />
              No form data available yet
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AdminReenrollmentReview;
